import { WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR } from "@/constants";
import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import {
    RewardsWaysToEarnB2bPageWidgetDefinition,
    RewardsWaysToEarnPageWidgetDefinition,
} from "@apps/rewards/constants/definitions";
import type { RewardsWidgetInjectorConfig } from "@apps/rewards/types";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import { parseOptionalBooleanDataAttr } from "@ui-utils/Injector/utils";

import RewardsWaysToEarnB2bPageWidget from "./b2b/RewardsWaysToEarnB2bPageWidget";
import RewardsWaysToEarnPageWidget from "./RewardsWaysToEarnPageWidget";

const GW_SHOW_POINTS_BALANCE = "gw-show-points-balance";

interface RewardsWaysToEarnPageWidgetInjectorConfig
    extends RewardsWidgetInjectorConfig {
    isCustomerB2b: boolean;
}

class RewardsWaysToEarnPageWidgetInjector {
    constructor(
        private readonly config: RewardsWaysToEarnPageWidgetInjectorConfig
    ) {}

    start(): void {
        this.config.logger.debug("RewardsWaysToEarnPageWidgetInjector start", {
            placeholderSelector: this.config.placeholderSelector,
        });

        const targets$ = this.config.mutationObserverInjector.register({
            targetSelector: this.config.placeholderSelector,
        });

        targets$.subscribe((placeholder) => {
            this.config.logger?.debug(
                "RewardsWaysToEarnPageWidgetInjector found placeholder",
                {
                    placeholder,
                }
            );

            let widget:
                | RewardsWaysToEarnPageWidget
                | RewardsWaysToEarnB2bPageWidget;
            let widgetName: REWARDS_WIDGET_NAMES;

            void (async () => {
                if (this.config.isCustomerB2b) {
                    const RewardsWaysToEarnB2bPageWidget =
                        await this.config.dynamicImportManager.importDefault(
                            RewardsWaysToEarnB2bPageWidgetDefinition,
                            true
                        );

                    widgetName =
                        REWARDS_WIDGET_NAMES.REWARDS_WAYS_TO_EARN_PAGE_WIDGET;
                    widget = new RewardsWaysToEarnB2bPageWidget(
                        {
                            target: placeholder,
                            isWidgetShadowRootEnabled:
                                parseOptionalBooleanDataAttr(
                                    placeholder,
                                    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                                ),
                            showPointsBalance: parseOptionalBooleanDataAttr(
                                placeholder,
                                GW_SHOW_POINTS_BALANCE
                            ),
                        },
                        this.config.logger
                    );
                } else {
                    const RewardsWaysToEarnPageWidget =
                        await this.config.dynamicImportManager.importDefault(
                            RewardsWaysToEarnPageWidgetDefinition,
                            true
                        );
                    widgetName =
                        REWARDS_WIDGET_NAMES.REWARDS_WAYS_TO_EARN_B2B_PAGE_WIDGET;
                    widget = new RewardsWaysToEarnPageWidget(
                        {
                            target: placeholder,
                            isWidgetShadowRootEnabled:
                                parseOptionalBooleanDataAttr(
                                    placeholder,
                                    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                                ),
                            showPointsBalance: parseOptionalBooleanDataAttr(
                                placeholder,
                                GW_SHOW_POINTS_BALANCE
                            ),
                        },
                        this.config.logger
                    );
                }

                await widget.init();

                this.config.eventsCollector.pushEvent(
                    this.config.eventsFactory.createWidgetInjected(widgetName)
                );

                dispatchSdkEvent(new WidgetInjectedSdkEvent(widgetName));
            })();
        });
    }
}

export default RewardsWaysToEarnPageWidgetInjector;
